import { BigNumber } from "@yjsgames/bignumber";
import { RELIC_DEFS_ORDERED } from "../data/balance.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";
import { ALL_MODULE_SYNERGY_DEFINITIONS } from "../data/moduleSynergyDefinitions.js";
import { SPACESHIP_DEFS } from "../data/spaceshipData.js";
import { chapterForWorld } from "../data/worldData.js";
import type { PieceRandomSource } from "../data/modulePieces.js";
import { connectedFillCells } from "../data/gridFillPath.js";
import { createModuleInstance, type InstanceIdSource } from "../models/ModuleInstance.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import { resolveModuleSynergies } from "./ModuleSynergyResolver.js";
import type { EconomyController } from "./EconomyController.js";
import type { RebirthController } from "./RebirthController.js";
import type { RelicController } from "./RelicController.js";
import type { DungeonController } from "./DungeonController.js";
import type { ModuleGridController } from "./ModuleGridController.js";
import type { ModuleInventory } from "./ModuleInventory.js";
import type { SpaceshipController } from "./SpaceshipController.js";
import type { DailyResetService } from "@yjsgames/daily-reset";

export const MAX_GOLD_GAIN_CHEAT_MULTIPLIER = 1_000_000;

const MODULE_SLOT_BY_ID = new Map<string, ModuleSlotType>(
  ALL_MODULE_DEFINITIONS.map((def) => [def.id, def.slotType as ModuleSlotType]),
);

interface SynergyPlacementPlan {
  readonly synergyId: string;
  readonly placements: readonly { definitionId: string; x: number }[];
}

export function normalizeGoldGainCheatMultiplier(multiplier: number): number {
  if (!Number.isFinite(multiplier) || multiplier < 1) return 1;
  return Math.min(multiplier, MAX_GOLD_GAIN_CHEAT_MULTIPLIER);
}

/**
 * CheatActions가 GameScene에 의존하는 최소 표면. controller는 getter(`() => ...`)로 받아
 * reload(applySaveData가 controller 재생성) 후에도 항상 최신 인스턴스를 참조한다(stale 방지).
 * chapter 재할당 + buildChapterGrid(렌더 의존)가 얽힌 setChapter는 GameScene이 콜백으로 제공한다.
 */
export interface CheatHost {
  readonly economy: () => EconomyController;
  readonly rebirth: () => RebirthController;
  readonly relic: () => RelicController;
  readonly dungeon: () => DungeonController;
  readonly dailyReset: () => DailyResetService;
  /** 광고 다이아 재구매 게이트 해제 — 2시간 쿨타임 clear + 오늘 수령 플래그 복귀(ShopAdCommands.resetGates). */
  readonly resetAdGates: () => void;
  /** 챕터 직접 설정 — 재할당 + 모듈 확정 지급 + 벽돌 재생성(GameScene 책임). */
  readonly setChapter: (n: number) => void;
  /** 모든 탭 잠금 해제 — bestChapter만 최대 해제 챕터로(현재 챕터·벽돌·모듈 불변, ChapterFlow.unlockAllTabs). */
  readonly unlockAllTabs: () => void;
  /** 우주선 보유 상태 — 모든 우주선 잠금 해제 치트에 사용. */
  readonly spaceship: () => SpaceshipController;
  /** 쿨타임 유물 반영 위해 발사 타이머 재설정. */
  readonly resetFireTimer: () => void;
  readonly refreshAll: () => void;
  readonly persist: () => void;
  /** 인하우스 광고 강제 토글 — 새 상태(true=강제 ON) 반환. 세션 한정(persist 안 함). */
  readonly toggleForceHouseAd: () => boolean;
  /** 미스터리 100% 강제 스폰 토글 — BattlefieldRunner 플래그 set + 현재 챕터 즉시 재빌드(직접 플레이 테스트). 세션 한정. */
  readonly setForceMysteryBricks: (enabled: boolean) => void;
  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 토글 — 러너 플래그 set + 현재 챕터 재빌드(QA). 세션 한정. */
  readonly setForceSpecialBricks: (enabled: boolean) => void;
  /** 모듈 조립 그리드 — 슬롯 전부 해제(unlockAll) + 배치(place)에 사용. */
  readonly grid: () => ModuleGridController;
  /** 모듈 인벤토리 — 치트로 생성한 인스턴스 추가에 사용. */
  readonly inventory: () => ModuleInventory;
  /** 인스턴스 id 생성기 — 새 모듈마다 고유 instanceId 발급. */
  readonly idSource: () => InstanceIdSource;
  /** 랜덤 정의 추첨 난수원 — 결정론 테스트 위해 주입형(GridCommands와 동일 소스). */
  readonly pieceRng: () => PieceRandomSource;
  /** dev 배너 미리보기 토글 — 웹 dev에서 배너 reserve 강제(레이아웃 확인). localStorage + reload, 새 값 반환. */
  readonly toggleBannerPreview: () => boolean;
  /** dev 배너 미리보기 현재 상태 (모달 초기 표시용). */
  readonly bannerPreviewEnabled: () => boolean;
  /** 광고제거(adFree) 해제 (dev 반복 테스트) — adFree=false + 저장 + 배너 재평가 + reload. */
  readonly clearAdFree: () => void;
  /** 강제 크래시 — Crashlytics 결선 검증용 비치명 예외 1건 기록(앱 종료 없음). 웹은 Noop. */
  readonly forceCrash: () => void;
  /** 챕터 패배 전환 연출 강제 실행 — 정상 패배와 같은 게임뷰 오버레이 확인용. */
  readonly triggerChapterDefeat: () => void;
  /** 환생 전환 연출 미리보기 — 저장/성장 상태 변경 없이 오버레이만 확인한다. */
  readonly previewRebirthTransition: () => void;
  /** 앱 시작 후 경과 시간 오버레이 토글 — 새 상태(true=ON) 반환. 세션 한정. */
  readonly toggleAppTimerOverlay: () => boolean;
  /** 앱 타이머 오버레이 현재 상태 (모달 초기 표시용). */
  readonly appTimerOverlayEnabled: () => boolean;
}

/**
 * DEV 치트 액션 — GameScene에서 추출(god-object 분해). 재화/통화/유물/도전권 직접 조작.
 * CheatModal·DebugDashboardModal 옵션에서 호출하며, autoplay(ModuleBrickGlobalExecutor·GameSceneAutoPlayView)에서도 scene.cheats로 직접 호출된다.
 * 도메인 조작 후 refreshAll + persist(commit)로 마무리.
 */
export class CheatActions {
  /** 전투 골드 획득 배율(세션 한정, 영속 X). QA용이라 저장하지 않는다. */
  private _goldGainMultiplier = 1;

  /** 미스터리 100% 강제 스폰 토글 상태(세션 한정, 영속 X). */
  private forceMystery = false;

  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 토글 상태(세션 한정, 영속 X). */
  private forceSpecial = false;

  constructor(private readonly host: CheatHost) {}

  giveRebirthPoint(n: number): void {
    this.host.rebirth().addPoint(n);
    this.commit();
  }

  addGold(amount: number): void {
    this.host.economy().addGold(BigNumber.fromNumber(Math.max(0, Math.floor(amount))));
    this.commit();
  }

  /** 전투 골드 획득 배율 설정. 세션 한정이며 1 미만/무효값은 1배로 복귀한다. */
  setGoldGainMultiplier(multiplier: number): number {
    this._goldGainMultiplier = normalizeGoldGainCheatMultiplier(multiplier);
    return this._goldGainMultiplier;
  }

  get goldGainMultiplier(): number {
    return this._goldGainMultiplier;
  }

  addDiamond(amount: number): void {
    this.host.economy().addDiamond(BigNumber.fromNumber(Math.max(0, Math.floor(amount))));
    this.commit();
  }

  setChapter(chapter: number): void {
    this.host.setChapter(Math.max(1, Math.floor(chapter)));
  }

  setWorld(worldNo: number): void {
    this.host.setChapter(chapterForWorld(worldNo));
  }

  /** 모든 탭 잠금 즉시 해제 (현재 챕터 유지). ChapterFlow.unlockAllTabs가 invalidateUi+refreshTabLocks+persist를 처리하므로 setChapter처럼 commit 위임 안 함(이중 persist 방지). */
  unlockAllTabs(): void {
    this.host.unlockAllTabs();
  }

  /** 모든 우주선을 즉시 보유 처리한다. 현재 장착 우주선과 그리드 배치는 바꾸지 않는다. */
  unlockAllSpaceships(): void {
    const spaceship = this.host.spaceship();
    for (const def of SPACESHIP_DEFS) spaceship.unlockSpaceship(def.id);
    this.commit();
  }

  /** 모든 유물 즉시 보유 (골드 차감 없음). 쿨타임 유물 포함이라 발사 타이머 재설정. */
  grantAllRelics(): void {
    const relic = this.host.relic();
    for (const def of RELIC_DEFS_ORDERED) relic.acquire(def.relicId);
    this.host.resetFireTimer();
    this.commit();
  }

  setTickets(n: number): void {
    this.host.dungeon().setTickets(n);
    this.commit();
  }

  /** 일일 리셋 강제 (자정 대기 없이 도전권 충전 사이클 검증). onReset 콜백이 자체 persist하므로 여기선 refreshAll만. */
  forceDailyReset(): void {
    this.host.dailyReset().forceResetAll();
    this.host.dailyReset().check();
    this.host.refreshAll();
  }

  /**
   * 광고 다이아 재구매 게이트 해제 (치트/QA) — 2시간 쿨타임 clear + 오늘 수령 플래그 복귀.
   * 자정·2시간 대기 없이 상점 광고 다이아 슬롯을 다시 구매 가능하게 만들어 인하우스 광고를 반복 확인한다.
   */
  resetAdGates(): void {
    this.host.resetAdGates();
    this.commit();
  }

  /** 인하우스 광고 강제 토글 — 라우팅이 항상 자사광고 선택하게(모바일도 100%). 새 상태 반환. */
  toggleForceHouseAd(): boolean {
    return this.host.toggleForceHouseAd();
  }

  /** 미스터리 100% 강제 스폰 토글 — 모든 챕터 일반 벽돌을 미스터리로(직접 플레이 테스트). 새 상태 반환. 세션 한정(재빌드가 화면 갱신하므로 commit 불필요). */
  toggleForceMystery(): boolean {
    this.forceMystery = !this.forceMystery;
    this.host.setForceMysteryBricks(this.forceMystery);
    return this.forceMystery;
  }

  /** 미스터리 강제 토글 현재 상태 (모달 초기 표시용). */
  get forceMysteryEnabled(): boolean {
    return this.forceMystery;
  }

  /** 특수 벽돌(방어막/장갑/재생) 강제 스폰 토글 — 챕터 무관 높은 확률로 마킹(QA). 새 상태 반환. 세션 한정. */
  toggleForceSpecial(): boolean {
    this.forceSpecial = !this.forceSpecial;
    this.host.setForceSpecialBricks(this.forceSpecial);
    return this.forceSpecial;
  }

  /** 특수 벽돌 강제 토글 현재 상태 (모달 초기 표시용). */
  get forceSpecialEnabled(): boolean {
    return this.forceSpecial;
  }

  /**
   * 그리드 슬롯 전부 해제 + 랜덤 모듈로 꽉 채우기 (DEV 치트). 기존 배치는 clear 후 새로 채운다.
   * 모양은 **Start→Fire 해밀턴 사행 경로**(connectedFillCells)를 따라 칸마다 실제 모양(직선/코너)으로
   * 결정한다 — 물이 25칸을 한 붓으로 전부 통과해 배치 모듈 전부가 발동하면서도 모양이 cross로 통일되지 않고
   * 다양하게 유지된다(랜덤 모양은 포트 불일치로 흐름이 막혀 발동 X였고, cross 통일은 모양이 다 같았다 — 둘 다 사용자 보고).
   * 효과만 정의별 랜덤으로 다양화한다. single footprint라 25칸을 빈틈없이 채운다.
   */
  fillGridRandomly(): void {
    const grid = this.host.grid();
    const inventory = this.host.inventory();
    const newId = this.host.idSource();
    const rng = this.host.pieceRng();
    const defs = ALL_MODULE_DEFINITIONS;

    // 직전 배치 인스턴스를 인벤토리에서도 회수(치트 반복 호출 시 미배치 인스턴스가 누적 팽창하는 것 방지) 후 초기화.
    for (const id of grid.placedInstanceIds()) inventory.removeInstance(id);
    grid.clearPlacedModules();
    grid.unlockAll();
    if (defs.length === 0) {
      this.commit();
      return;
    }

    // 사행 경로 셀+모양(연결 보장) × 정의 랜덤(효과 다양). 경로 순서대로 배치.
    for (const cell of connectedFillCells(grid)) {
      const def = defs[Math.floor(rng() * defs.length)]!;
      const inst = inventory.addInstance(
        createModuleInstance({
          definitionId: def.id,
          instanceId: newId(),
          shapeId: cell.shapeId,
          footprintId: "single",
        }),
      );
      grid.place(cell.x, cell.y, inst.instanceId, inst.definitionId, inst.shapeId, inst.footprintId);
    }
    this.commit();
  }

  /**
   * 시작행의 빈 칸에 아직 발동되지 않은 랜덤 시너지의 부족 파이프만 연속 배치한다.
   * 반복 실행해도 기존 배치는 유지하므로 여러 시너지가 동시에 발동되는 상태를 빠르게 만들 수 있다.
   */
  placeRandomSynergyPipes(): string | null {
    const grid = this.host.grid();
    const inventory = this.host.inventory();
    const rng = this.host.pieceRng();
    const y = grid.start.y;

    grid.unlockAll();

    const placedByCell = new Map(grid.placed().map((tile) => [`${tile.x},${tile.y}`, tile] as const));
    const eligible = ALL_MODULE_SYNERGY_DEFINITIONS.flatMap((def): SynergyPlacementPlan[] => {
      const windowWidth = def.requiredModuleIds.length;
      if (windowWidth <= 0 || windowWidth > grid.width) return [];

      const plans: SynergyPlacementPlan[] = [];
      for (let startX = 0; startX <= grid.width - windowWidth; startX++) {
        const placements: { definitionId: string; x: number }[] = [];
        let blocked = false;
        for (let i = 0; i < windowWidth; i++) {
          const definitionId = def.requiredModuleIds[i]!;
          const x = startX + i;
          const existing = placedByCell.get(`${x},${y}`);
          if (existing) {
            if (existing.definitionId !== definitionId) blocked = true;
            continue;
          }
          if (!grid.canPlace(x, y, "single")) blocked = true;
          placements.push({ definitionId, x });
        }
        if (blocked || placements.length === 0) continue;

        const plannedLineSlots = Array.from({ length: grid.width }, (_, x) => {
          const existing = placedByCell.get(`${x},${y}`)?.definitionId;
          const planned = placements.find((p) => p.x === x)?.definitionId;
          const definitionId = existing ?? planned ?? null;
          return definitionId ? { definitionId, x } : null;
        }).filter((slot): slot is { definitionId: string; x: number } => slot !== null);
        const plannedLineIds = plannedLineSlots.map((slot) => slot.definitionId);
        const matched = resolveModuleSynergies({
          activeModuleIds: plannedLineIds,
          activeSlotTypes: plannedLineIds
            .map((id) => MODULE_SLOT_BY_ID.get(id))
            .filter((slot): slot is NonNullable<typeof slot> => slot !== undefined),
          activeModuleGroups: plannedLineIds.map((id) => [id]),
          activeModuleSlots: plannedLineSlots.map((slot) => ({
            definitionIds: [slot.definitionId],
            cells: [{ x: slot.x, y }],
          })),
        }).matched;
        if (matched.some((synergy) => synergy.id === def.id)) {
          plans.push({ synergyId: def.id, placements });
        }
      }
      return plans;
    });
    if (eligible.length === 0) return null;

    const plan = eligible[Math.floor(rng() * eligible.length)]!;
    const newId = this.host.idSource();

    plan.placements.forEach(({ definitionId, x }) => {
      const inst = inventory.addInstance(
        createModuleInstance({
          definitionId,
          instanceId: newId(),
          shapeId: "straight_h",
          footprintId: "single",
        }),
      );
      grid.place(x, y, inst.instanceId, inst.definitionId, inst.shapeId, inst.footprintId);
    });

    this.commit();
    return plan.synergyId;
  }

  /** dev 배너 미리보기 토글 — 웹 dev에서 배너 reserve 강제(빌드 없이 레이아웃 확인). 새 값 반환(reload 직전). */
  toggleBannerPreview(): boolean {
    return this.host.toggleBannerPreview();
  }

  /** dev 배너 미리보기 현재 상태 (모달 초기 표시용). */
  get bannerPreviewEnabled(): boolean {
    return this.host.bannerPreviewEnabled();
  }

  /** 광고제거(adFree) 해제 (dev) — 구매 효과(배너 사라짐+전체화면) 반복 테스트용. setAdFree(false) + reload. */
  clearAdFree(): void {
    this.host.clearAdFree();
  }

  /**
   * 강제 크래시 — Crashlytics 결선 검증(dev). recordException 비치명 1건을 보고한다(앱 종료 없이 콘솔/대시보드
   * 확인). 치트 모달 버튼 + AutoPlay FORCE_CRASH 공용. 웹은 Noop port라 무해.
   */
  forceCrash(): void {
    this.host.forceCrash();
  }

  /** 챕터 패배 연출 확인 — 정상 전투 패배 경로와 같은 overlay/우주선 파괴 시퀀스를 호출한다. */
  triggerChapterDefeat(): void {
    this.host.triggerChapterDefeat();
  }

  /** 환생 연출 확인 — 실제 환생 조건/저장 상태는 건드리지 않고 전환 overlay만 재생한다. */
  previewRebirthTransition(): void {
    this.host.previewRebirthTransition();
  }

  /** 앱 시작 후 경과 시간 오버레이 토글 — QA 치트 전용, 저장하지 않는다. */
  toggleAppTimerOverlay(): boolean {
    return this.host.toggleAppTimerOverlay();
  }

  /** 앱 타이머 오버레이 현재 상태. */
  get appTimerOverlayEnabled(): boolean {
    return this.host.appTimerOverlayEnabled();
  }

  private commit(): void {
    this.host.refreshAll();
    this.host.persist();
  }
}
